/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { bindable } from 'aurelia-typed-observable-plugin';
import { persist } from 'app/shared/lib/persist-decorator';
import { caseIsReadOnly } from 'app/cm/lib/utilities';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { SaveCase } from 'app/cm/components/case/save-case';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { openContactLog } from 'app/cd/components/contact-log/contact-log';
import { openMemberProviderRelationships } from 'app/cd/components/member/member-providers-grid';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { MemberService } from 'app/cd/services/member-service';
import { openCaseAuditLog as openAuditLog } from 'app/cm/components/audit-log/case-audit-log';
import { CustomFieldDefinition } from 'app/core/models/essette-api';
import { ITickEvent, TickHandler, TickService } from 'app/shared/services/event-services';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';

interface ICaseNavAccess {
    caseFile: boolean;
    contactLog: boolean;
    auditLog: boolean;
    memberProviderRelationships: boolean;
    elli: boolean;
    medications: boolean;
    healthwise: boolean;
    campaigns: boolean;
    pam: boolean;
    mara: boolean;
}

@autoinject()
export class CaseRibbon extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    protected case: CM.Case | null = null;

    @bindable.array({ defaultBindingMode: bindingMode.oneTime })
    protected acuities: CM.Acuity[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.oneTime })
    protected intensities: CM.Intensity[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.oneTime })
    protected programGroups: CM.ProgramGroup[] = [];

    protected $isValid!: boolean;
    protected validate = validate;

    protected canEditCaseDetails: boolean = false;
    protected caseNavAccess: ICaseNavAccess | null = null;
    protected showCaseSummaryLink: boolean = false;
    protected isCMOfflineEnabled: boolean = false;
    protected hasCaseRoute = false;
    protected caseDefinitionChunks: CustomFieldDefinition[][] = [];
    protected openCaseSummary = openCaseSummary;
    protected openContactLog = openContactLog;
    protected openAuditLog = openAuditLog;
    protected openMemberProviderRelationships = openMemberProviderRelationships;

    protected contactLogConfig: IContactLogConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM',
        rollupConfig: {
            serviceRootUrl: 'cd/members',
            targetType: 'CD.Member'
        }
    };

    @persist({ initialValue: false })
    protected ribbonExpanded!: boolean;
    protected OpenFlags = OpenFlags;

    protected lastUpdate: number;
    protected readonly updateCaseModifiedTimestamp: TickHandler;

    ////****************** Element References ***************************************////
    protected $modal!: IPopup;
    protected $vmSaveCase!: SaveCase;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly caseService: CaseService,
        private readonly memberService: MemberService,
        protected readonly ticker: TickService,
        ...rest: any[]) {

        super(...rest);

        const updateDuration = this.env.config.pageConfigurations.cm.case.ribbon.modifiedDateUpdateIntervalSeconds * 1000; // seconds to milliseconds
        this.lastUpdate = Date.now() - (0.75 * updateDuration); // don't do first update right away
        this.updateCaseModifiedTimestamp = (e: ITickEvent) => {
            if(e.time > this.lastUpdate + updateDuration) {
                if(!this.state.busy) this.loadCaseModifiedDate();
            }
        }
    }

    protected bind(): void {
        if(!this.case || !this.case.id) { throw new Error('Missing bindable property [case]'); }

        this.checkPermissions();
        this.configureDefinitionChunks();

        this.ticker.addEventListener('tick', this.updateCaseModifiedTimestamp);
    }

    protected unbind(): void {
        this.ticker.removeEventListener('tick', this.updateCaseModifiedTimestamp);
    }

    ////****************** Events ***************************************************////
    protected caseChanged(): void {
        if(!this.case || !this.case.id) return;
        this.checkPermissions();
        this.configureDefinitionChunks();
    }

    protected formSuccess(): void {
        if(!!this.$modal) {
            this.$modal.close();
            this.checkPermissions();
        }
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        const hasEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
        const hasEditCaseDetails = this.accountContext.hasPermission('CM.Case.Update.EditCaseDetails');
        const readonly = this.case ? caseIsReadOnly(this.case, this.env.user.id) : true;
        this.canEditCaseDetails = readonly ? hasEditClosedCase : hasEditCaseDetails;
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        this.isCMOfflineEnabled = this.env.config.integrations.includes('cm-offline');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');

        this.caseNavAccess = {
            caseFile: this.accountContext.hasRouteAccess('3125'),
            contactLog: this.accountContext.hasRouteAccess('cm.case.contact-log'),
            auditLog: this.accountContext.hasRouteAccess('cm.case.audit-log'),
            memberProviderRelationships: this.accountContext.hasRouteAccess('cm.member-provider-relationships'),
            medications: this.accountContext.hasRouteAccess('3062'),
            healthwise: this.accountContext.hasRouteAccess('7000'),
            campaigns: this.accountContext.hasRouteAccess('14083'),
            pam: this.accountContext.hasRouteAccess('22001'),
            mara: this.accountContext.hasRouteAccess('15001'),
            elli: this.accountContext.hasRouteAccess('elli.member-profile')
        } as ICaseNavAccess;
    }

    private configureDefinitionChunks(): void {
        this.caseDefinitionChunks.splice(0);
        const definitions = this.case?.custom?.target?.definitions;
        if(!definitions?.length) return;
        for(let i = 0; i < definitions.length; i += 5) {
            let definitionsChunk = definitions.slice(i, i + 5);
            this.caseDefinitionChunks.push(definitionsChunk);
        }
    }

    protected toggleExpand(): void {
        this.ribbonExpanded = !this.ribbonExpanded;
    }

    protected async openCaseContactLog(): Promise<void> {
        if(!this.case || !this.case.id || !this.case.member || !this.case.member.id) { return; }
        await this.loadMemberContactInfo(this.case.member.id);
        this.openContactLog(this.case.member, this.contactLogConfig, this.case.id, undefined, true);
    }

    protected async openCaseAuditLog(): Promise<void> {
        this.openAuditLog(this.case);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberContactInfo(memberId: string): Promise<void> {
        if(!memberId) return;

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMember(memberId);
            if(!this.case!.member) {
                this.case!.member = {};
            }
            Object.assign(this.case!.member, response.results[0]);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    protected async loadCaseModifiedDate(): Promise<void> {
        if(!this.case?.id) return;

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCase(this.case.id, undefined, ['metadata.rollupModified.timestamp']);
            if(!this.case.metadata) this.case.metadata = {};
            if(!this.case.metadata.rollupModified) this.case.metadata.rollupModified = { timestamp: null };
            this.case.metadata.rollupModified.timestamp = response.results[0].metadata?.rollupModified?.timestamp || this.case.metadata.rollupModified.timestamp;
        } catch(error) {
            throw error;
        } finally {
            this.lastUpdate = Date.now();
            this.state.endLoad();
        }
    }
}
